export interface DiscoverLink {
  text: string;
  href: string;
}

export interface MoreLink {
  text: string;
  href: string;
}

export interface StoreBadgeItem {
  href: string;
  badge: string;
  alt: string;
}

export interface SocialChannel {
  name: string;
  href: string;
  icon: string;
}

export interface TranslationData {
  lang: 'en' | 'ar';
  dir: 'ltr' | 'rtl';
  title: string;
  langLabel: string;
  targetLang: string;
  targetPath: string;
  loadingText: string;
  deploymentId: string;
  environment: string;
  logoAlt: string;
  homeLink: string;
  discover: {
    title: string;
    links: DiscoverLink[];
  };
  more: {
    title: string;
    links: MoreLink[];
  };
  appDownload: {
    title: string;
    googlePlay: StoreBadgeItem;
    appStore: StoreBadgeItem;
  };
  social: {
    title: string;
    channels: SocialChannel[];
  };
  copyright: string;
}

export const translations: Record<'en' | 'ar', TranslationData> = {
  en: {
    lang: 'en',
    dir: 'ltr',
    title: 'Jawwy Helpdesk',
    langLabel: 'العربية',
    targetLang: 'ar',
    targetPath: '/ar',
    loadingText: 'Loading...',
    deploymentId: '09a8a53f-87fa-4b89-8d37-32916d77a195',
    environment: 'prod-euc1',
    logoAlt: 'Jawwy',
    homeLink: '/',
    discover: {
      title: 'Discover',
      links: [
        { text: 'Shop', href: 'https://www.jawwy.sa/content/jawwy/en/shop.html' },
        { text: 'Roaming', href: 'https://www.jawwy.sa/content/jawwy/en/roaming.html' },
        { text: 'Community', href: 'https://community.jawwy.sa/' },
        { text: 'Store Locator', href: 'https://www.stc.com.sa/en/personal/support/contact-us/business-offices-coverage-maps.html#mapview' }
      ]
    },
    more: {
      title: 'More',
      links: [
        { text: 'About Us', href: 'https://www.jawwy.sa/content/jawwy/en/about.html' },
        { text: 'Privacy Policy', href: 'https://www.stc.com.sa/en/content-page/privacy.html' },
        { text: 'Terms & Conditions', href: 'https://www.jawwy.sa/content/jawwy/en/terms.html' },
        { text: 'VAT Registration Certificate', href: 'https://s3.eu-central-1.amazonaws.com/static.jawwy.sa/emad-3B5800AF-89CC-4EDC-8F0C-074679622F7F/Documents/VAT-registration-certificate.pdf' }
      ]
    },
    appDownload: {
      title: 'Download Jawwy app',
      googlePlay: {
        href: 'https://play.google.com/store/apps/details?id=com.jawwyevo_main.app&hl=en&gl=US',
        badge: '/assets/helpdesk/Store Badge-2.svg',
        alt: 'Google Play Store'
      },
      appStore: {
        href: 'https://apps.apple.com/sa/app/jawwy-%D8%AC%D9%88-%D9%8A-2-0/id1670373086',
        badge: '/assets/helpdesk/Store Badge.svg',
        alt: 'Apple App Store'
      }
    },
    social: {
      title: 'Follow Us',
      channels: [
        { name: 'Facebook', href: 'https://www.facebook.com/Jawwy', icon: '/assets/helpdesk/Social Icons.svg' },
        { name: 'X', href: 'https://x.com/jawwy', icon: '/assets/helpdesk/Social Icons-5.svg' },
        { name: 'Youtube', href: 'https://www.youtube.com/channel/UCOg0jwxivctGjYmJ6yCh4cg', icon: '/assets/helpdesk/Social Icons-2.svg' },
        { name: 'Instagram', href: 'https://www.instagram.com/Jawwy/', icon: '/assets/helpdesk/Social Icons-3.svg' },
        { name: 'Snapchat', href: 'https://www.snapchat.com/add/jawwy', icon: '/assets/helpdesk/Social Icons-4.svg' }
      ]
    },
    copyright: '© Jawwy from stc'
  },
  ar: {
    lang: 'ar',
    dir: 'rtl',
    title: 'مكتب مساعدة جوّي',
    langLabel: 'English',
    targetLang: 'en',
    targetPath: '/',
    loadingText: 'تحميل ...',
    deploymentId: 'faadaf7a-6f59-4903-bba0-856545d59089',
    environment: 'prod-euc1',
    logoAlt: 'جوّي',
    homeLink: 'https://jawwy.sa',
    discover: {
      title: 'اكتشف',
      links: [
        { text: 'تسوق', href: 'https://www.jawwy.sa/content/jawwy/en/shop.html' },
        { text: 'التجوال', href: 'https://www.jawwy.sa/content/jawwy/en/roaming.html' },
        { text: 'المجتمع', href: 'https://community.jawwy.sa/' },
        { text: 'محدد المواقع', href: 'https://www.stc.com.sa/en/personal/support/contact-us/business-offices-coverage-maps.html#mapview' }
      ]
    },
    more: {
      title: 'المزيد',
      links: [
        { text: 'من نحن', href: 'https://www.jawwy.sa/content/jawwy/en/about.html' },
        { text: 'سياسة الخصوصية', href: 'https://www.stc.com.sa/en/content-page/privacy.html' },
        { text: 'الشروط والأحكام', href: 'https://www.jawwy.sa/content/jawwy/en/terms.html' },
        { text: 'شهادة التسجيل في ضريبة القيمة المضافة', href: 'https://s3.eu-central-1.amazonaws.com/static.jawwy.sa/emad-3B5800AF-89CC-4EDC-8F0C-074679622F7F/Documents/VAT-registration-certificate.pdf' }
      ]
    },
    appDownload: {
      title: 'حمل تطبيق جوّي',
      googlePlay: {
        href: 'https://play.google.com/store/apps/details?id=com.jawwyevo_main.app&hl=en&gl=US',
        badge: '/assets/helpdesk/Store Badge-2.svg',
        alt: 'متجر جوجل بلاي'
      },
      appStore: {
        href: 'https://apps.apple.com/sa/app/jawwy-%D8%AC%D9%88-%D9%8A-2-0/id1670373086',
        badge: '/assets/helpdesk/Store Badge.svg',
        alt: 'متجر آبل'
      }
    },
    social: {
      title: 'تابعنا',
      channels: [
        { name: 'فيسبوك', href: 'https://www.facebook.com/Jawwy', icon: '/assets/helpdesk/Social Icons.svg' },
        { name: 'إكس', href: 'https://x.com/jawwy', icon: '/assets/helpdesk/Social Icons-5.svg' },
        { name: 'يوتيوب', href: 'https://www.youtube.com/channel/UCOg0jwxivctGjYmJ6yCh4cg', icon: '/assets/helpdesk/Social Icons-2.svg' },
        { name: 'إنستغرام', href: 'https://www.instagram.com/Jawwy/', icon: '/assets/helpdesk/Social Icons-3.svg' },
        { name: 'سناب شات', href: 'https://www.snapchat.com/add/jawwy', icon: '/assets/helpdesk/Social Icons-4.svg' }
      ]
    },
    copyright: '© جوّي من stc'
  }
};
